import { CommonModule } from '@angular/common';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { Component, inject, OnInit, ElementRef, Renderer2 } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PagesService } from '@services/pages.service';
import { SharedService } from '@services/shared.service';
import { SharedModule } from '@shared/shared.module';
import { AuthService } from 'src/app/Auth/AuthService';
import { PlanService } from '@services/plan.service';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { PrivacyPolicyComponent } from '@pages/Outer/pages/privacy-policy/privacy-policy.component';
import { TermsConditionComponent } from '@pages/Outer/pages/terms-condition/terms-condition.component';

@Component({
  selector: 'app-subscription',
  standalone: true,
  imports: [SharedModule, CommonModule, NzModalModule, ReactiveFormsModule, NzSelectModule],
  templateUrl: './subscription.component.html',
  styleUrls: ['./subscription.component.scss'],
})
export class SubscriptionComponent implements OnInit {
  authService = inject(AuthService);
  sharedService = inject(SharedService);
  pagesService = inject(PagesService);
  planService = inject(PlanService);
  router = inject(Router);
  route = inject(ActivatedRoute);
  renderer = inject(Renderer2);
  elementRef = inject(ElementRef);
  modal = inject(NzModalService);
  subscribedPlans: any = {};
  activeTabId: 'monthly' | 'annually' = 'monthly';
  selectedPlan: any = {};
  isVisible = false;
  monthlyPlans: any[] = [];
  annualPlans: any[] = [];
  enterprizePlans: any[] = [];
  isLoggedIn = false;
  isPaymentModal = false;
  selectedPaymentMethod: string = '';
  renewModal: boolean = false;
  enterprizeModal: boolean = false;
  enterprizeForm!: FormGroup;
  agreeTerms: boolean = false;
  user: any;
  remainingTime: any;
  isLoadingPlans: boolean = true;
  isFreePlanModalVisible = false;
  selectedFreePlan: any = null;
  private accessDeniedShown = false;
  planChangeType: 'upgrade' | 'downgrade' | '' = '';

  constructor(private fb: FormBuilder) {
    if (this.authService.isLoggedIn()) {
      this.isLoggedIn = true;
    }
    // ✅ Only check query params once on component initialization
    this.route.queryParams.subscribe((params) => {
      if (params['accessDenied'] === 'true' && !this.accessDeniedShown) {
        this.accessDeniedShown = true;
        this.sharedService.createNotification(
          'error',
          'You must subscribe to access this feature.'
        );
        // Clear the query parameter to prevent showing the message again
        this.router.navigate([], {
          queryParams: { accessDenied: null },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      }
    });
  }
  ngOnInit() {
    this.user = this.authService.getUserDetails();
    this.enterprizeForm = this.fb.group({
      plan_id: [null],
      name: [
        '',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.pattern('^[a-zA-Z ]+$')
        ]
      ],
      email: [
        '',
        [
          Validators.required,
          Validators.email
        ]
      ],
      phone_number: [
        '',
        [
          Validators.required,
        ]
      ],
      company_name: ['', Validators.required],
      address: ['', Validators.required],
      payment_method: ['', Validators.required],
      details: ['', Validators.required]
    });

    if (this.isLoggedIn) {
      this.getSubscribedPlans();
    }
    this.getSubscriptionPlans();
  }

  freePlanApi(planId: number) {
    const user = this.authService.getUserDetails();
    if (!user) return;
    const payload = {
      user_id: user.id,
      plan_id: planId
    };
    this.sharedService.showLoader();
    this.pagesService.create('FREE_PLAN', payload).subscribe({
      next: (res: any) => {
        this.sharedService.hideLoader();
        this.sharedService.createNotification(
          'success',
          'Free plan subscribed successfully'
        );

        // ✅ Update premimum_user flag in localStorage
        const userDetailsRaw = localStorage.getItem('userDetails');
        if (userDetailsRaw) {
          const userDetails = JSON.parse(userDetailsRaw);
          userDetails.premimum_user = true;
          localStorage.setItem('userDetails', JSON.stringify(userDetails));
        }

        this.getSubscribedPlans();
      },
      error: (err: any) => {
        this.sharedService.hideLoader();

        // Check multiple possible error structures
        const errorCode = err?.error?.code || err?.code;
        const errorMessage = err?.error?.message || err?.message;

        if (errorCode === 'ERROR_FATAL') {
          this.sharedService.createNotification(
            'error',
            errorMessage || 'Failed to subscribe to free plan. Please try again.'
          );
        } else {
          // Show generic error message if it's a different error
          this.sharedService.createNotification(
            'error',
            errorMessage || 'Failed to subscribe to free plan. Please try again.'
          );
        }
      }
    });
  }
  // free plan timmer function
  isFreePlan(): boolean {
    if (!this.subscribedPlans || !this.subscribedPlans.sub_plan) {
      return false;
    }
    const subPlan = this.subscribedPlans.sub_plan;
    const bothPricesFree = subPlan.monthly_price === 0 && subPlan.yearly_price === 0;
    const descriptionContainsFree = subPlan.description &&
      subPlan.description.toLowerCase().includes('free');
    const planNameContainsFree = subPlan.plan_name &&
      subPlan.plan_name.toLowerCase().includes('free');
    return bothPricesFree || descriptionContainsFree || planNameContainsFree;
  }
  showModal(): void {
    this.isVisible = true;
  }
  handleOk(): void {
    this.isVisible = false;
    this.isPaymentModal = true;
    this.planChangeType = '';
  }
  handlePaymentOk(): void {
    if (!this.selectedPaymentMethod) {
      this.sharedService.createNotification(
        'error',
        'Please select a payment method.'
      );
      return;
    }
    this.isPaymentModal = false;
    const isRenewFlow = !!this.selectedPlan?.isRenewal;

    if (isRenewFlow) {
      this.renewCurrentPlan(this.selectedPlan.plan_id, this.selectedPaymentMethod);
      return;
    }

    if (this.selectedPaymentMethod === 'online') {
      this.subscribAPlan(this.selectedPlan.plan_id);
    } else if (this.selectedPaymentMethod === 'cash') {
      this.sharedService.createNotification(
        'info',
        'For cash payment, please contact our admin or business team.'
      );
      this.cashPayment(this.selectedPlan.plan_id);
    }
  }
  cashPayment(plan_id: string) {
    if (!this.isLoggedIn) {
      this.sharedService.createNotification(
        'warning',
        'Please login to continue with cash payment.'
      );
      this.router.navigate(['auth/login']);
      return;
    }
    const payload = {
      plan_id,
      type: this.activeTabId
    };
    this.sharedService.showLoader();
    this.pagesService.create('CASH_PAYMENT', payload).subscribe({
      next: (Response: any) => {
        this.sharedService.hideLoader();
        this.isPaymentModal = false;
        // this.sharedService.createNotification(
        //   'success',
        //   'Cash payment request submitted successfully. Our team will contact you soon.'
        // );
      },
      error: (error: any) => {
        this.sharedService.hideLoader();
        this.sharedService.createNotification(
          'error',
          'Failed to initiate cash payment.'
        );
      },
    });
  }
  handleCancel(): void {
    this.isVisible = false;
    this.selectedPlan = null;
    this.planChangeType = '';
  }
  handleRenewCancel(): void {
    this.renewModal = false;
  }
  handleEnterprizeCancel(): void {
    this.enterprizeModal = false;
    this.enterprizeForm.reset();
  }
  handleRenewOk(): void {
    this.renewModal = false;
    this.isPaymentModal = true;
  }
  handleEnterprizeOk(): void {
    if (!this.enterprizeForm.valid) {
      this.sharedService.hideLoader();
      this.enterprizeForm.markAllAsTouched();
      return;
    }
    const form = this.enterprizeForm;
    const basePayload = {
      plan_id: form.get('plan_id')?.value,
      name: form.get('name')?.value,
      email: form.get('email')?.value,
      phone_number: form.get('phone_number')?.value,
      details: form.get('details')?.value,
      company_name: form.get('company_name')?.value,
      address: form.get('address')?.value,
      payment_method: form.get('payment_method')?.value,
    };
    this.sharedService.showLoader();
    this.pagesService.update('ENTERPRISE_PLAN', basePayload).subscribe({
      next: () => {
        this.sharedService.createNotification(
          'success',
          'Your enterprise request has been submitted successfully.'
        );
        this.enterprizeForm.reset();
        this.enterprizeModal = false;
        this.sharedService.hideLoader();
      },
      error: () => {
        this.sharedService.createNotification(
          'error',
          'Unable to submit your request at this time. Please try again later.'
        );
        this.enterprizeForm.reset();
        this.sharedService.hideLoader();
      },
    });
  }
  handlePaymentCancel(): void {
    this.isPaymentModal = false;
    this.selectedPaymentMethod = '';
  }
  subscribe(plan_id: string, type: string, mode: string, price: string) {
    if (!this.isLoggedIn) {
      this.sharedService.createNotification(
        'warning',
        'Please login to subscribe to a plan.'
      );
      this.router.navigate(['auth/login']);
      return;
    }
    this.selectedPlan = { plan_id, type, mode, price };
    if (this.subscribedPlans && this.subscribedPlans.type) {
      this.isVisible = true;
    } else {
      this.isPaymentModal = true;
    }
  }
  subscribAPlan(plan_id: string) {
    const type = {
      plan_id,
      success_url: `${this.sharedService.getBaseUrl()}/home/subscription/detail?session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${this.sharedService.getBaseUrl()}/home/subscription`,
      type: this.activeTabId,
    };
    this.sharedService.showLoader();
    this.authService.login('CHOOSE_SUBSCRIPTION_PLAN', type).subscribe({
      next: (Response: any) => {
        setTimeout(() => {
          this.sharedService.hideLoader();
          if (Response.checkout_url) {
            window.location.href = Response.checkout_url;
          } else {
            this.sharedService.createNotification(
              'error',
              'Checkout URL not found.'
            );
          }
        }, 1000);
        const alertsJson = localStorage.getItem('app_alerts');
        if (alertsJson) {
          let alerts = JSON.parse(alertsJson);
          alerts = alerts.filter(
            (alert: any) => alert.action !== 'subscription'
          );
          localStorage.setItem('app_alerts', JSON.stringify(alerts));
        }
        this.sharedService.closeAlertByAction('subscription');
      },
      error: (error: any) => {
        this.sharedService.hideLoader();
        this.sharedService.createNotification(
          'error',
          'Failed to initiate subscription.'
        );
      },
    });
  }
  getSubscribedPlans() {
    this.isLoadingPlans = true;
    this.sharedService.showLoader();
    this.authService.getAll('PURCHASED_PLAN').subscribe({
      next: (Response: any) => {
        this.subscribedPlans = Response || null;

        if (this.subscribedPlans) {
          const isExpiredFromApi = typeof this.subscribedPlans?.isExpired === 'boolean'
            ? this.subscribedPlans.isExpired
            : null;

          // If API explicitly marks expired, honor it; otherwise calculate days left
          this.remainingTime = isExpiredFromApi === true
            ? { days: 0, expired: true }
            : this.calculateRemainingTime(this.subscribedPlans);

          if (this.remainingTime.expired) {
            this.sharedService.createNotification(
              'warning',
              'Your plan has expired. Please renew your current plan.'
            );
          }
        }

        const userPlan = Response?.report_view?.toLowerCase() || 'none';
        this.sharedService.setPlanType(userPlan);
        this.isLoadingPlans = false;
        this.sharedService.hideLoader();
        this.setActiveTabBasedOnSubscription();
      },
      error: (error: any) => {
        this.isLoadingPlans = false;
        this.sharedService.hideLoader();

        if (error && error.message === 'User is not subscribed yet') {
          this.subscribedPlans = {};
          this.sharedService.setPlanType('none');
        }
      },
    });
  }

  getSubscriptionPlans() {
    const getOrderIndex = (price: any): number => {
      if (price == null) return Number.MAX_SAFE_INTEGER;
      if (typeof price === 'string') {
        const lower = price.toLowerCase().trim();
        if (lower === 'free') return 0;
        if (lower.includes('contact')) return Number.MAX_SAFE_INTEGER;
        const numeric = parseFloat(lower.replace(/[^0-9.]/g, ''));
        return isNaN(numeric) ? Number.MAX_SAFE_INTEGER : numeric;
      }
      return price;
    };
    const sortPlans = (plans: any[], priceKey: 'monthly_price' | 'yearly_price') => {
      return plans.sort((a: any, b: any) => {
        // Enterprise always last
        if (a.isEnterprise !== b.isEnterprise) {
          return a.isEnterprise ? 1 : -1;
        }
        // Then sort by monthly/yearly price
        return getOrderIndex(a?.[priceKey]) - getOrderIndex(b?.[priceKey]);
      });
    };
    this.sharedService.showLoader();
    this.pagesService.getAll('GET_SUBSCRIPTION_PLAN', {
      page: 1,
      per_page: 1000
    }).subscribe({
      next: (response: any) => {
        this.sharedService.hideLoader();
        const plans = Array.isArray(response)
          ? response
          : Array.isArray(response?.items)
            ? response.items
            : Array.isArray(response?.data)
              ? response.data
              : [];

        if (plans.length > 0) {
          const activePlans = plans.filter(
            (plan: any) => plan?.status?.toLowerCase() === 'active'
          );
          // Use monthly_price & yearly_price instead of mode
          this.monthlyPlans = sortPlans(activePlans, 'monthly_price');
          this.annualPlans = sortPlans(activePlans, 'yearly_price');

          // Enterprise plans separately if you want
          this.enterprizePlans = activePlans.filter((plan: any) => plan.isEnterprise);
          // ✅ Yahan expiry check karo
          if (this.remainingTime?.expired) {
            this.sharedService.createNotification(
              'warning',
              '⚠️ Your subscription has expired. Please renew your plan.'
            );
          }
        } else {
          this.sharedService.createNotification('error', 'No subscription plans available.');
        }
      },
      error: () => {
        this.sharedService.hideLoader();
      },
    });
  }
  isEnterprizePlan(plan: any): boolean {
    return plan?.isEnterprise === true;
  }
  setActiveTabBasedOnSubscription() {
    if (this.subscribedPlans && this.subscribedPlans.mode) {
      if (
        this.subscribedPlans.mode.toLowerCase() === 'annually' ||
        this.subscribedPlans.mode.toLowerCase() === 'yearly'
      ) {
        this.activeTabId = 'annually';
      } else {
        this.activeTabId = 'monthly';
      }
    } else {
      this.activeTabId = 'monthly';
    }
    this.updateBootstrapTabActivation();
  }
  updateBootstrapTabActivation() {
    if (typeof window !== 'undefined' && this.elementRef) {
      setTimeout(() => {
        const monthlyTabButton = this.elementRef.nativeElement.querySelector('#monthly-tab');
        const yearlyTabButton = this.elementRef.nativeElement.querySelector('#yearly-tab');
        const enterprizeTabButton = this.elementRef.nativeElement.querySelector('#enterprize-tab');
        const monthlyTabPane = this.elementRef.nativeElement.querySelector('#monthly');
        const yearlyTabPane = this.elementRef.nativeElement.querySelector('#yearly');
        const enterprizeTabPane = this.elementRef.nativeElement.querySelector('#enterprize');
        const tabs = [
          { id: 'monthly', button: monthlyTabButton, pane: monthlyTabPane },
          { id: 'annually', button: yearlyTabButton, pane: yearlyTabPane },
          { id: 'enterprize', button: enterprizeTabButton, pane: enterprizeTabPane },
        ];
        tabs.forEach(tab => {
          const isActive = this.activeTabId === tab.id;
          if (tab.button && tab.pane) {
            this.renderer[isActive ? 'addClass' : 'removeClass'](tab.button, 'active');
            this.renderer.setAttribute(tab.button, 'aria-selected', isActive.toString());
            this.renderer[isActive ? 'addClass' : 'removeClass'](tab.pane, 'show');
            this.renderer[isActive ? 'addClass' : 'removeClass'](tab.pane, 'active');
          }
        });
      }, 0);
    }
  }
  selectTab(tabId: 'monthly' | 'annually') {
    this.activeTabId = tabId;
    this.updateBootstrapTabActivation();
  }
  isCurrentPlan(id: number, mode: string): boolean {
    if (this.remainingTime?.expired) return false; // ✅ yahan se badge chala jayega
    const currentType = this.subscribedPlans?.plan_id;
    const currentMode = this.subscribedPlans?.mode?.toLowerCase();
    return currentType === id && currentMode === mode.toLowerCase();
  }
  renewSubscribtion() {
    this.renewModal = true
  }

  isPlanExpired(): boolean {
    return !!(this.subscribedPlans?.isExpired === true || this.remainingTime?.expired);
  }

  isSamePlan(plan: any): boolean {
    return !!(
      this.subscribedPlans &&
      this.subscribedPlans.plan_id !== undefined &&
      Number(this.subscribedPlans.plan_id) === Number(plan?.id)
    );
  }

  shouldShowRenew(plan: any): boolean {
    return this.isLoggedIn && this.isSamePlan(plan) && !plan?.isEnterprise;
  }

  getPlanActionLabel(plan: any): string {
    if (this.shouldShowRenew(plan)) return 'Renew';
    if (this.getPlanComparison(plan.id) === 'same') return 'Current Plan';

    if (!this.hasActiveSubscription()) return 'Subscribe';

    return this.getPlanComparison(plan.id) === 'upgrade' ? 'Upgrade' : 'Downgrade';
  }

  renewCurrentPlan(plan: any, paymentMethod: string = 'online') {
    const user = this.authService.getUserDetails();

    if (!user?.id) {
      this.sharedService.createNotification('warning', 'Please login to renew your plan.');
      this.router.navigate(['auth/login']);
      return;
    }

    const payload = {
      plan_id: this.subscribedPlans?.plan_id || plan?.id,
      user_id: user.id,
      billing_type: this.activeTabId === 'annually' ? 'annually' : 'monthly',
      payment_method: paymentMethod || 'online',
      success_url: `${this.sharedService.getBaseUrl()}/auth/payment-approved`,
      cancel_url: `${this.sharedService.getBaseUrl()}/auth/payment-rejected`
    };

    this.sharedService.showLoader();
    this.pagesService.create('RENEWAL_PAYMENT_LINK', payload).subscribe({
      next: (response: any) => {
        this.sharedService.hideLoader();
        if (response?.checkout_url) {
          window.location.href = response.checkout_url;
          return;
        }
        if (payload.payment_method === 'cash') {
          this.sharedService.createNotification(
            'success',
            'Renewal request submitted successfully. Our team will contact you for cash payment.'
          );
          return;
        }
        this.sharedService.createNotification('error', 'Checkout URL not found.');
      },
      error: () => {
        this.sharedService.hideLoader();
        this.sharedService.createNotification('error', 'Failed to initiate renewal.');
      }
    });
  }

  handleClick(plan: any) {
    if (this.shouldShowRenew(plan)) {
      const mode = this.activeTabId === 'annually' ? 'annually' : 'monthly';
      const price = mode === 'annually' ? plan.yearly_price : plan.monthly_price;
      this.selectedPlan = {
        plan_id: plan.id,
        type: plan.type,
        plan_name: plan.plan_name,
        mode: mode,
        price: price,
        currency: plan.currency || 'USD',
        isRenewal: true
      };
      this.renewModal = true;
      return;
    }

    if (plan.isEnterprise) {
      this.EnterprizeSubscribtion();
      this.enterprizeForm.get('plan_id')?.setValue(plan?.id);
      if (this.user) {
        this.enterprizeForm.patchValue({
          name: this.user.name || '',
          company_name: this.user.isCompany?.[0]?.name || this.user._company_as_client?.name || '',
          email: this.user.email || '',
          phone_number: this.user.phone_number || ''
        });
      }
    }
    // Free plan case
    else if (
      (plan.monthly_price === 0 && plan.yearly_price === 0) ||
      plan.plan_name.toLowerCase().includes('free')
    ) {
      this.selectedFreePlan = plan;
      this.isFreePlanModalVisible = true;
    }
    else {
      // Determine the mode and price based on active tab
      const mode = this.activeTabId === 'annually' ? 'annually' : 'monthly';
      const price = mode === 'annually' ? plan.yearly_price : plan.monthly_price;
      const hasValidSubscription = this.subscribedPlans &&
        Object.keys(this.subscribedPlans).length > 0 &&
        this.subscribedPlans.plan_id !== undefined;
      if (hasValidSubscription && !this.remainingTime?.expired) {
        this.selectedPlan = {
          plan_id: plan.id,
          type: plan.type,
          plan_name: plan.plan_name,
          mode: mode,
          price: price,
          monthly_price: plan.monthly_price,
          yearly_price: plan.yearly_price,
          currency: plan.currency || 'USD'
        };

        const comparison = this.getPlanComparison(plan.id);
        this.planChangeType = comparison === 'upgrade' ? 'upgrade' :
          comparison === 'downgrade' ? 'downgrade' : '';

        this.isVisible = true;
      }
      else {
        this.selectedPlan = {
          plan_id: plan.id,
          type: plan.type,
          plan_name: plan.plan_name,
          mode: mode,
          price: price
        };
        this.isPaymentModal = true;
      }
    }
  }
  handleFreePlanOk() {
    this.isFreePlanModalVisible = false;
    if (this.selectedFreePlan?.id) {
      this.freePlanApi(this.selectedFreePlan.id); // Pass the dynamic ID
    }
  }
  handleFreePlanCancel() {
    this.isFreePlanModalVisible = false;
  }
  EnterprizeSubscribtion() {
    this.enterprizeModal = true
  }
  hasActiveSubscription(): boolean {
    const currentSubscription = this.subscribedPlans;
    const hasValidSubscription = currentSubscription &&
      Object.keys(currentSubscription).length > 0 &&
      currentSubscription.plan_id !== undefined;
    return hasValidSubscription && !this.remainingTime?.expired;
  }

  getPlanComparison(planId: number): 'upgrade' | 'downgrade' | 'same' | '' {
    if (this.remainingTime?.expired) {
      return '';
    }
    const allPlans = [...this.monthlyPlans, ...this.annualPlans];
    const newPlan = allPlans.find(p => p.id === planId);
    const currentSubscription = this.subscribedPlans;
    // If subscribedPlans is an empty object or doesn't have a plan_id, user is not subscribed
    const hasValidSubscription = currentSubscription &&
      Object.keys(currentSubscription).length > 0 &&
      currentSubscription.plan_id !== undefined;
    if (!newPlan || !hasValidSubscription) return 'upgrade';
    if (currentSubscription.plan_id === planId) {
      const currentMode = currentSubscription.mode?.toLowerCase();
      const selectedMode = this.activeTabId === 'annually' ? 'annually' : 'monthly';

      if ((currentMode === 'annually' || currentMode === 'yearly') && selectedMode === 'annually') {
        return 'same';
      } else if (currentMode !== 'annually' && currentMode !== 'yearly' && selectedMode === 'monthly') {
        return 'same';
      }
    }
    if (newPlan.isEnterprise || currentSubscription.sub_plan?.isEnterprise) {
      if (currentSubscription.plan_id === planId) {
        return 'same';
      }
      return 'upgrade';
    }
    const selectedCycle: 'monthly_price' | 'yearly_price' =
      this.activeTabId === 'annually' ? 'yearly_price' : 'monthly_price';
    const subscribedCycle: 'monthly_price' | 'yearly_price' =
      (currentSubscription.mode?.toLowerCase() === 'annually' ||
        currentSubscription.mode?.toLowerCase() === 'yearly')
        ? 'yearly_price'
        : 'monthly_price';
    const currentPrice = this.getPriceValue(currentSubscription.sub_plan?.[subscribedCycle]);
    const newPrice = this.getPriceValue(newPlan[selectedCycle]);

    if (subscribedCycle !== selectedCycle) {
      if (selectedCycle === 'yearly_price') {
        return newPrice >= currentPrice ? 'upgrade' : 'downgrade';
      } else {
        return newPrice <= currentPrice ? 'downgrade' : 'upgrade';
      }
    }
    if (newPrice > currentPrice) return 'upgrade';
    if (newPrice < currentPrice) return 'downgrade';
    return 'same';
  }
  private getPriceValue(price: any): number {
    if (price == null || price === undefined) return 0;

    if (typeof price === 'string') {
      const lower = price.toLowerCase().trim();

      if (lower === 'free' || lower === '') return 0;
      if (lower.includes('contact') || lower.includes('enterprise')) return Number.MAX_SAFE_INTEGER;

      const numeric = parseFloat(lower.replace(/[^0-9.]/g, ''));
      return isNaN(numeric) ? 0 : numeric;
    }
    if (typeof price === 'number') return price;
    return 0;
  }
  calculateRemainingTime(plan: any): {
    days: number;
    expired: boolean;
  } {
    if (!plan?.start_date || !plan?.end_date) {
      return { days: 0, expired: true };
    }

    const now = new Date();
    const endDate = new Date(plan.end_date);

    // ✅ End date ko midnight set karein taake pure din ka calculation ho
    endDate.setHours(23, 59, 59, 999);

    const diff = endDate.getTime() - now.getTime();

    if (diff <= 0) {
      return { days: 0, expired: true };
    }

    // ✅ Total seconds calculate karein
    const totalSeconds = Math.floor(diff / 1000);

    // ✅ Days calculate karein
    const days = Math.floor(totalSeconds / (60 * 60 * 24));

    return { days, expired: false };
  }

  getDiscountedPrice(price: number, discountType: string, discountValue: number): number {
    if (!discountType || !discountValue) return price;

    if (discountType === 'percentage') {
      return price - (price * discountValue / 100);
    } else if (discountType === 'fixed') {
      return price - discountValue;
    }
    return price;
  }

  private getActiveRenewalDiscount(plan: any): any | null {
    const discounts = plan?.subscription_renewal_discount_of_user_of_billing_payment;
    if (!Array.isArray(discounts) || discounts.length === 0) return null;

    const now = Date.now();
    const active = discounts.find((item: any) => {
      const status = String(item?.status || '').toLowerCase();
      const validFrom = Number(item?.valid_from || 0);
      const validUntil = Number(item?.valid_until || 0);
      const withinValidity =
        validFrom > 0 && validUntil > 0 ? now >= validFrom && now <= validUntil : true;

      return status === 'active' && withinValidity;
    });

    return active || null;
  }

  hasRenewalDiscount(plan: any, cycle: 'monthly' | 'yearly'): boolean {
    const discount = this.getActiveRenewalDiscount(plan);
    if (!discount) return false;

    const discountedAmount = cycle === 'monthly'
      ? Number(discount?.discounted_monthly_amount)
      : Number(discount?.discounted_yearly_amount);
    const originalAmount = cycle === 'monthly'
      ? Number(discount?.original_monthly_amount || plan?.monthly_price || 0)
      : Number(discount?.original_yearly_amount || plan?.yearly_price || 0);

    return !Number.isNaN(discountedAmount) && discountedAmount >= 0 && discountedAmount < originalAmount;
  }

  getRenewalDiscountAmount(plan: any, cycle: 'monthly' | 'yearly'): number {
    const discount = this.getActiveRenewalDiscount(plan);
    if (!discount) return cycle === 'monthly' ? Number(plan?.monthly_price || 0) : Number(plan?.yearly_price || 0);

    const value = cycle === 'monthly'
      ? Number(discount?.discounted_monthly_amount)
      : Number(discount?.discounted_yearly_amount);
    return Number.isNaN(value) ? 0 : value;
  }

  getOriginalPlanAmount(plan: any, cycle: 'monthly' | 'yearly'): number {
    const discount = this.getActiveRenewalDiscount(plan);
    const fallback = cycle === 'monthly' ? Number(plan?.monthly_price || 0) : Number(plan?.yearly_price || 0);
    if (!discount) return fallback;

    const original = cycle === 'monthly'
      ? Number(discount?.original_monthly_amount)
      : Number(discount?.original_yearly_amount);
    return Number.isNaN(original) ? fallback : original;
  }

  getDiscountValue(plan: any): number {
    if (this.activeTabId === 'monthly' || plan.mode === 'monthly') {
      return plan.monthly_discount || 0;
    } else if (this.activeTabId === 'annually' || plan.mode === 'annually' || this.activeTabId === 'yearly') {
      return plan.yearly_discount || 0;
    }
    return 0;
  }

  getPlanPrice(plan: any): number {
    if (this.activeTabId === 'monthly' || plan.mode === 'monthly') {
      return plan.monthly_price;
    } else if (this.activeTabId === 'annually' || plan.mode === 'annually' || this.activeTabId === 'yearly') {
      return plan.yearly_price;
    }
    return 0;
  }
  calculateDiscount(plan: any): { type: string, value: number } {
    const discountValue = this.getDiscountValue(plan);
    return {
      type: plan.discount_type,
      value: discountValue
    };
  }
  openPrivacyPolicy(): void {
    this.modal.create({
      nzTitle: 'Privacy Policy',
      nzContent: PrivacyPolicyComponent,
      nzFooter: null,
      nzWidth: 1000,
      nzMaskClosable: true,
      nzClosable: true,
      nzWrapClassName: 'custom-modal-animate',
      nzMaskStyle: { backgroundColor: 'rgba(0,0,0,0.6)' },
      nzNoAnimation: false,
      nzBodyStyle: {
        maxHeight: '90vh',
        overflowY: 'auto'
      },
    });
  }
  openTermsConditions(): void {
    this.modal.create({
      nzTitle: 'Terms & Conditions',
      nzContent: TermsConditionComponent,
      nzFooter: null,
      nzWidth: 1000,
      nzMaskClosable: true,
      nzClosable: true,
      nzWrapClassName: 'custom-modal-animate',
      nzMaskStyle: { backgroundColor: 'rgba(0,0,0,0.6)' },
      nzNoAnimation: false,
      nzBodyStyle: {
        maxHeight: '90vh',
        overflowY: 'auto'
      },
    });
  }

}

